import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Target, Smartphone, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import HeroArtwork from "@/components/site/HeroArtwork";

export default function Hero() {
  const { t } = useApp();
  const go = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });

  // 2x2 order requested:
  //   Row 1: Built around you | One central system
  //   Row 2: Secure & scalable | Any device
  const benefits = [
    { icon: Zap, label: t("hero.fast") },
    { icon: Target, label: t("hero.convert") },
    { icon: ShieldCheck, label: t("hero.seo") },
    { icon: Smartphone, label: t("hero.mobile") },
  ];

  return (
    <section
      data-testid="hero-section"
      className="relative pt-2 md:pt-4 pb-10 md:pb-12 min-h-[calc(100vh-160px)] md:min-h-[calc(100vh-170px)] flex items-center"
    >
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-8 xl:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-4 xl:gap-6 items-center">
          {/* LEFT: eyebrow, headline, copy, ctas, benefits */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <p
              data-testid="hero-eyebrow"
              className="text-[11px] tracking-[0.28em] uppercase text-white font-bold mb-4 drop-shadow-[0_1px_0_rgba(0,0,0,0.15)]"
            >
              {t("hero.eyebrow")}
            </p>
            <h1 className="font-display font-black uppercase leading-[0.92] tracking-[-0.035em]">
              <span
                data-testid="hero-line1"
                className="block md:whitespace-nowrap text-black text-[clamp(2rem,5.2vw,4rem)]"
              >
                {t("hero.line1")}
              </span>
              <span
                data-testid="hero-line2"
                className="block md:whitespace-nowrap text-white text-[clamp(2rem,5.2vw,4rem)] drop-shadow-[0_2px_0_rgba(0,0,0,0.12)]"
              >
                {t("hero.line2")}
              </span>
            </h1>
            {/* White accent line under ONE SYSTEM. */}
            <div
              data-testid="hero-accent-line"
              className="mt-3 h-[3px] w-14 bg-white rounded-full"
            />

            <p className="mt-5 text-[15px] md:text-base text-black font-semibold max-w-md leading-snug">
              {t("hero.sub")}
            </p>
            <p className="mt-2 text-[13px] md:text-[15px] text-black/80 font-medium max-w-md leading-relaxed">
              {t("hero.secondary")}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <motion.button
                data-testid="hero-cta-primary"
                onClick={() => go("#discovery")}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group inline-flex items-center gap-3 h-11 md:h-12 px-6 md:px-7 rounded-full bg-black text-white font-display font-bold text-[13px] md:text-sm tracking-wide uppercase hover:bg-black/90 transition-all shadow-[0_16px_38px_-14px_rgba(0,0,0,0.45)]"
              >
                {t("hero.cta_primary")}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform rtl:-scale-x-100" />
              </motion.button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => go("#services")}
                className="inline-flex items-center gap-2 h-11 md:h-12 px-5 md:px-6 rounded-full bg-[#FFFDF6] border border-black/15 text-black font-display font-bold text-[13px] md:text-sm tracking-wide uppercase hover:bg-white transition-all"
              >
                {t("hero.cta_secondary")}
              </button>
            </div>

            {/* Benefits — 2x2 on md+, single column on mobile */}
            <div
              data-testid="hero-benefits"
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5 max-w-md"
            >
              {benefits.map((b) => (
                <div key={b.label} className="flex items-center gap-2.5 text-black">
                  <div className="w-7 h-7 rounded-full bg-white grid place-items-center shadow-[0_4px_10px_-4px_rgba(0,0,0,0.2)]">
                    <b.icon size={13} strokeWidth={2.4} className="text-black" />
                  </div>
                  <span className="text-[12.5px] md:text-[13px] font-semibold tracking-wide">
                    {b.label}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT: hero artwork — oversized, pulled left, static */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 flex items-center justify-start lg:-ml-6 xl:-ml-10"
          >
            <HeroArtwork />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
